import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  RadialBarChart, RadialBar, Cell
} from 'recharts';
import {
  DollarSign, ShoppingBag, TrendingUp, Receipt,
  Table2, Ban, RefreshCw, Loader2, Package,
  CreditCard, Utensils, Star, ArrowUpRight,
  ArrowDownRight, Users, Zap, BarChart2, Clock
} from 'lucide-react';
import Navbar from '../components/Navbar';
import '../assets/css/dashboard.css';
import axios from 'axios';
import { baseurl } from '../baseurl';

/* ─────────────────────────────────────────
   API
───────────────────────────────────────── */
const api = () => {
  const t = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${t}` } });
};

/* ─────────────────────────────────────────
   FORMATTERS (IST)
───────────────────────────────────────── */
const fmtIST = d => new Date(d).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
  year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
});

const fmtDateShort = d => new Date(d).toLocaleDateString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
});

const fmt  = n => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtD = n => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = n => parseInt(n || 0).toLocaleString('en-IN');
const pct  = (a, b) => b > 0 ? ((parseFloat(a) / parseFloat(b)) * 100).toFixed(1) : '0';

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const PERIODS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week',      label: '7 Days' },
  { id: 'month',     label: '30 Days' },
];

const PAY_COLORS = { cash: '#34c759', card: '#007aff', upi: '#5856d6', split: '#ff9f0a' };
const SRC_COLORS = { pos: '#ff3b1f', qr: '#5856d6', online: '#007aff' };

/* ─────────────────────────────────────────
   CUSTOM TOOLTIP
───────────────────────────────────────── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
      fontFamily: "'Outfit', sans-serif", fontSize: 12,
      minWidth: 130,
    }}>
      <div style={{ fontWeight: 700, color: '#111', marginBottom: 6, fontSize: 11.5 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }}/>
          <span style={{ color: '#6e6e73', fontWeight: 600, flex: 1 }}>{p.name}:</span>
          <span style={{ fontFamily: "'DM Mono', monospace", color: '#111', fontWeight: 500 }}>
            {p.name?.toLowerCase().includes('rev') || p.name?.toLowerCase().includes('revenue')
              ? fmt(p.value) : fmtN(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   DONUT
───────────────────────────────────────── */
function PayDonut({ segments, totalVal, totalLabel }) {
  const total = segments.reduce((s, x) => s + parseFloat(x.value || 0), 0);
  let cum = 0;
  const conic = total > 0
    ? segments.map(s => {
        const deg = (parseFloat(s.value) / total) * 360;
        const f = cum; cum += deg;
        return `${s.color} ${f}deg ${cum}deg`;
      }).join(', ')
    : '#f5f5f7 0deg 360deg';

  return (
    <div className="db-donut-container">
      <div className="db-donut-wheel">
        <div style={{
          width: 120, height: 120, borderRadius: '50%',
          background: `conic-gradient(${conic})`,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.07)',
        }}/>
        <div className="db-donut-inner">
          <div className="db-donut-val">{totalVal}</div>
          <div className="db-donut-sub">{totalLabel}</div>
        </div>
      </div>
      <div className="db-pay-list">
        {segments.map((s, i) => (
          <div key={i} className="db-pay-item">
            <div className="db-pay-pill" style={{ background: s.color }}/>
            <span className="db-pay-label">{s.label}</span>
            <span className="db-pay-val">{fmt(s.value)}</span>
            <span className="db-pay-pct">{pct(s.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN DASHBOARD
───────────────────────────────────────── */
export default function Dashboard() {
  const [period,  setPeriod]  = useState('today');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [ts,      setTs]      = useState('');

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await api().get('/reports/dashboard', { params: { period: p } });
      setData(r.data);
      setTs(fmtIST(new Date()));
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const s       = data?.summary       || {};
  const tblLive = data?.table_status  || {};

  /* Chart data */
  const chartData = useMemo(() => {
    if (!data) return [];
    if (period === 'today' || period === 'yesterday') {
      return (data.hourly || []).map(h => ({
        label:   `${h.hour}:00`,
        Revenue: parseFloat(h.revenue || 0),
        Orders:  parseInt(h.orders   || 0),
      }));
    }
    return (data.daily || []).map(d => ({
      label:   fmtDateShort(d.date),
      Revenue: parseFloat(d.revenue || 0),
      Orders:  parseInt(d.orders   || 0),
    }));
  }, [data, period]);

  const paySegs = useMemo(() => [
    { label: 'Cash',  value: s.cash_total  || 0, color: PAY_COLORS.cash  },
    { label: 'Card',  value: s.card_total  || 0, color: PAY_COLORS.card  },
    { label: 'UPI',   value: s.upi_total   || 0, color: PAY_COLORS.upi   },
    { label: 'Split', value: s.split_total || 0, color: PAY_COLORS.split },
  ].filter(x => parseFloat(x.value) > 0), [s]);

  const sources   = useMemo(() => (data?.sources    || []).map(src => ({
    name: src.order_source || 'pos', count: parseInt(src.count || 0), amount: parseFloat(src.amount || 0),
  })), [data]);

  const topItems  = useMemo(() => (data?.top_items  || []).slice(0, 7), [data]);
  const topTables = useMemo(() => (data?.top_tables || []).slice(0, 5), [data]);
  const maxQty    = useMemo(() => Math.max(...topItems.map(i => parseInt(i.qty_sold || 0)), 1), [topItems]);
  const maxTableRev = useMemo(() => parseFloat(topTables[0]?.revenue || 1), [topTables]);

  const revChange = parseFloat(s.revenue_change || 0);
  const restoName = localStorage.getItem('restaurant_name') || 'Restaurant';

  /* Type-to-color for order source */
  const srcTotal = sources.reduce((a, x) => a + x.count, 0);

  return (
    <div className="db-root">
      <Navbar />

      <div className="db-page">

        {/* ── TOP BAR ── */}
        <div className="db-topbar db-animate db-animate-1">
          <div>
            <div className="db-brand-tag">
              <div className="db-brand-dot"/>
              Live Dashboard
            </div>
            <h1 className="db-page-title">
              {restoName} — <em>Analytics</em>
            </h1>
            {ts && <div className="db-page-sub">Updated {ts}</div>}
          </div>
          <div className="db-controls">
            <div className="db-period-group">
              {PERIODS.map(p => (
                <button key={p.id}
                  className={`db-period-btn ${period === p.id ? 'active' : ''}`}
                  onClick={() => setPeriod(p.id)}>
                  {p.label}
                </button>
              ))}
            </div>
            <button
              className={`db-icon-btn ${loading ? 'spin' : ''}`}
              onClick={() => load(period)} disabled={loading}>
              {loading ? <Loader2 size={15} className="db-spin"/> : <RefreshCw size={15}/>}
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="db-loading">
            <Loader2 size={32} className="db-spin"/>
            <p>Loading dashboard…</p>
          </div>
        )}

        {data && (
          <>
            {/* ── HERO ROW ── */}
            <div className="db-hero-row db-animate db-animate-2">

              {/* Main revenue callout */}
              <div className="db-hero-card">
                <div className="db-hero-tag">Total Revenue</div>
                <div className="db-hero-amount">{fmt(s.total_revenue)}</div>
                <div className="db-hero-footer">
                  <div className="db-hero-change">
                    {revChange > 0
                      ? <><ArrowUpRight size={12}/> +{revChange}%</>
                      : revChange < 0
                      ? <><ArrowDownRight size={12}/> {revChange}%</>
                      : <><Zap size={11}/> Live</>
                    }
                  </div>
                  <div className="db-hero-meta">{fmtN(s.confirmed_orders)} confirmed orders</div>
                </div>
              </div>

              {/* Orders stat card */}
              <div className="db-stat-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="db-stat-icon"
                    style={{ background: 'rgba(0,122,255,0.08)', color: '#007aff' }}>
                    <ShoppingBag size={17}/>
                  </div>
                  {parseInt(s.cancelled_orders) > 0 && (
                    <span className="db-stat-badge neu"
                      style={{ background: 'rgba(255,45,85,0.08)', color: '#ff2d55' }}>
                      {fmtN(s.cancelled_orders)} cancelled
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div className="db-stat-label">Total Orders</div>
                  <div className="db-stat-value">{fmtN(s.total_orders)}</div>
                  <div className="db-stat-sub">{fmtN(s.confirmed_orders)} confirmed</div>
                </div>
                <div className="db-stat-card-bg" style={{ background: '#007aff' }}/>
              </div>

              {/* Avg order card */}
              <div className="db-stat-card">
                <div className="db-stat-icon"
                  style={{ background: 'rgba(52,199,89,0.08)', color: '#34c759' }}>
                  <TrendingUp size={17}/>
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div className="db-stat-label">Avg Order Value</div>
                  <div className="db-stat-value">{fmt(s.avg_order_value)}</div>
                  <div className="db-stat-sub">per confirmed order</div>
                  <span className="db-stat-badge up">
                    <ArrowUpRight size={9}/> Performing
                  </span>
                </div>
                <div className="db-stat-card-bg" style={{ background: '#34c759' }}/>
              </div>
            </div>

            {/* ── KPI STRIP ── */}
            <div className="db-kpi-strip db-animate db-animate-3">
              {[
                {
                  icon: <Receipt size={16}/>, bg: 'rgba(88,86,214,0.08)', col: '#5856d6',
                  label: 'GST Collected', val: fmt(s.total_tax), sub: 'tax collected',
                },
                {
                  icon: <Table2 size={16}/>, bg: 'rgba(255,159,10,0.09)', col: '#ff9f0a',
                  label: 'Tables Occupied',
                  val: `${tblLive.occupied || 0} / ${(tblLive.occupied || 0) + (tblLive.available || 0) + (tblLive.reserved || 0)}`,
                  sub: `${tblLive.available || 0} available`,
                },
                {
                  icon: <Ban size={16}/>, bg: 'rgba(255,45,85,0.08)', col: '#ff2d55',
                  label: 'Cancellations', val: fmtN(s.cancelled_orders), sub: 'orders cancelled',
                },
                {
                  icon: <Star size={16}/>, bg: 'rgba(255,159,10,0.09)', col: '#ff9f0a',
                  label: 'Discounts', val: fmt(s.total_discount || 0), sub: 'total discount given',
                },
              ].map((k, i) => (
                <div key={i} className="db-kpi-mini">
                  <div className="db-kpi-mini-icon" style={{ background: k.bg, color: k.col }}>
                    {k.icon}
                  </div>
                  <div className="db-kpi-mini-body">
                    <div className="db-kpi-mini-label">{k.label}</div>
                    <div className="db-kpi-mini-val">{k.val}</div>
                    <div className="db-kpi-mini-sub">{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── BODY GRID ── */}
            <div className="db-body-grid db-animate db-animate-4">

              {/* LEFT COLUMN */}
              <div className="db-col">

                {/* Revenue area chart */}
                <div className="db-card">
                  <div className="db-card-hd">
                    <div className="db-card-title">
                      <TrendingUp size={14}/> Revenue Trend
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div className="db-chart-legend">
                        <div className="db-chart-legend-item">
                          <div className="db-chart-legend-dot" style={{ background: '#ff3b1f' }}/>
                          Revenue
                        </div>
                        <div className="db-chart-legend-item">
                          <div className="db-chart-legend-dot" style={{ background: '#007aff' }}/>
                          Orders
                        </div>
                      </div>
                      <div className="db-card-meta">
                        {period === 'today' || period === 'yesterday' ? 'Hourly' : 'Daily'}
                      </div>
                    </div>
                  </div>
                  <div className="db-card-bd">
                    <div className="db-chart-main">
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
                            <defs>
                              <linearGradient id="gRev2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#ff3b1f" stopOpacity={0.15}/>
                                <stop offset="95%" stopColor="#ff3b1f" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="gOrd2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#007aff" stopOpacity={0.12}/>
                                <stop offset="95%" stopColor="#007aff" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)"/>
                            <XAxis dataKey="label" axisLine={false} tickLine={false}
                              tick={{ fill: '#aeaeb2', fontSize: 10, fontFamily: "'DM Mono', monospace" }}/>
                            <YAxis yAxisId="rev" axisLine={false} tickLine={false}
                              tick={{ fill: '#aeaeb2', fontSize: 10, fontFamily: "'DM Mono', monospace" }}
                              tickFormatter={v => v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`}/>
                            <YAxis yAxisId="ord" orientation="right" axisLine={false} tickLine={false}
                              tick={{ fill: '#aeaeb2', fontSize: 10, fontFamily: "'DM Mono', monospace" }}/>
                            <Tooltip content={<ChartTooltip/>}/>
                            <Area yAxisId="rev" type="monotone" dataKey="Revenue"
                              stroke="#ff3b1f" strokeWidth={2.5} fill="url(#gRev2)"
                              dot={false} activeDot={{ r: 5, fill: '#ff3b1f', stroke: '#fff', strokeWidth: 2 }}/>
                            <Area yAxisId="ord" type="monotone" dataKey="Orders"
                              stroke="#007aff" strokeWidth={2} fill="url(#gOrd2)"
                              dot={false} activeDot={{ r: 4, fill: '#007aff', stroke: '#fff', strokeWidth: 2 }}/>
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="db-empty"><TrendingUp size={28}/><p>No data for this period</p></div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mini 2-col: Orders bar + Table status */}
                <div className="db-mini-2col">

                  {/* Orders bar chart */}
                  <div className="db-card">
                    <div className="db-card-hd">
                      <div className="db-card-title"><BarChart2 size={13}/> Orders Volume</div>
                    </div>
                    <div className="db-card-bd">
                      <div className="db-chart-mini">
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.slice(-12)} barSize={7}
                              margin={{ top: 4, right: 2, bottom: 0, left: -10 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)"/>
                              <XAxis dataKey="label" axisLine={false} tickLine={false}
                                tick={{ fill: '#aeaeb2', fontSize: 9, fontFamily: "'DM Mono', monospace" }}/>
                              <YAxis axisLine={false} tickLine={false}
                                tick={{ fill: '#aeaeb2', fontSize: 9, fontFamily: "'DM Mono', monospace" }}/>
                              <Tooltip content={<ChartTooltip/>}/>
                              <Bar dataKey="Orders" radius={[3,3,0,0]}>
                                {chartData.slice(-12).map((_, i, arr) => (
                                  <Cell key={i}
                                    fill={i === arr.length - 1 ? '#ff3b1f' : '#007aff'}
                                    fillOpacity={i === arr.length - 1 ? 0.9 : 0.65}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="db-empty" style={{ padding: 20 }}><p>No data</p></div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table status */}
                  <div className="db-card">
                    <div className="db-card-hd">
                      <div className="db-card-title"><Table2 size={13}/> Table Status</div>
                      <div className="db-card-meta">Live</div>
                    </div>
                    <div className="db-card-bd">
                      <div className="db-table-status-row">
                        {[
                          { cls: 'occupied',  n: tblLive.occupied  || 0, label: 'Occupied' },
                          { cls: 'available', n: tblLive.available || 0, label: 'Free' },
                          { cls: 'reserved',  n: tblLive.reserved  || 0, label: 'Reserved' },
                        ].map((t, i) => (
                          <div key={i} className={`db-ts ${t.cls}`}>
                            <div className="db-ts-num">{t.n}</div>
                            <div className="db-ts-label">{t.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Selling Items */}
                <div className="db-card">
                  <div className="db-card-hd">
                    <div className="db-card-title"><Star size={14}/> Top Selling Items</div>
                    <div className="db-card-meta">{topItems.length} items · {period}</div>
                  </div>
                  <div className="db-card-bd">
                    {topItems.length > 0 ? topItems.map((item, i) => (
                      <div key={i} className="db-item-row">
                        <div className={`db-item-rank ${i === 0 ? 'gold' : ''}`}>
                          {i === 0 ? '★' : `${i + 1}`}
                        </div>
                        <div className="db-item-info">
                          <div className="db-item-name">
                            {item.name}{item.variant === 'half' ? ' ½' : ''}
                          </div>
                          <div className="db-item-cat">{item.variant} plate</div>
                        </div>
                        <div className="db-item-bar-track">
                          <div className="db-item-bar-fill"
                            style={{ width: `${pct(parseInt(item.qty_sold), maxQty)}%` }}/>
                        </div>
                        <div className="db-item-qty">{fmtN(item.qty_sold)} sold</div>
                        <div className="db-item-rev">{fmt(item.revenue)}</div>
                      </div>
                    )) : (
                      <div className="db-empty"><Package size={24}/><p>No item data</p></div>
                    )}
                  </div>
                </div>

              </div>

              {/* RIGHT COLUMN */}
              <div className="db-col">

                {/* Payment methods donut */}
                <div className="db-card">
                  <div className="db-card-hd">
                    <div className="db-card-title"><CreditCard size={14}/> Payment Methods</div>
                    <div className="db-card-meta">{fmt(s.total_revenue)}</div>
                  </div>
                  <div className="db-card-bd">
                    {paySegs.length > 0 ? (
                      <PayDonut
                        segments={paySegs}
                        totalVal={fmt(s.total_revenue)}
                        totalLabel="Revenue"
                      />
                    ) : (
                      <div className="db-empty"><CreditCard size={24}/><p>No payment data</p></div>
                    )}
                  </div>
                </div>

                {/* Revenue sparkline mini */}
                {chartData.length > 1 && (
                  <div className="db-card">
                    <div className="db-card-hd">
                      <div className="db-card-title"><Zap size={14}/> Revenue Sparkline</div>
                    </div>
                    <div className="db-card-bd">
                      <div className="db-chart-sm">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -30 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)"/>
                            <XAxis dataKey="label" axisLine={false} tickLine={false}
                              tick={{ fill: '#aeaeb2', fontSize: 9, fontFamily: "'DM Mono', monospace" }}/>
                            <YAxis axisLine={false} tickLine={false}
                              tick={{ fill: '#aeaeb2', fontSize: 9, fontFamily: "'DM Mono', monospace" }}
                              tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}/>
                            <Tooltip content={<ChartTooltip/>}/>
                            <Line type="monotone" dataKey="Revenue" stroke="#ff3b1f" strokeWidth={2}
                              dot={{ r: 3, fill: '#ff3b1f', stroke: '#fff', strokeWidth: 1.5 }}
                              activeDot={{ r: 5, fill: '#ff3b1f', stroke: '#fff', strokeWidth: 2 }}/>
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order sources */}
                {sources.length > 0 && (
                  <div className="db-card">
                    <div className="db-card-hd">
                      <div className="db-card-title"><Utensils size={14}/> Order Sources</div>
                      <div className="db-card-meta">{fmtN(srcTotal)} total</div>
                    </div>
                    <div className="db-card-bd">
                      {sources.map((src, i) => (
                        <div key={i} className="db-src-row">
                          <div className="db-src-dot" style={{ background: SRC_COLORS[src.name] || '#8a8a8e' }}/>
                          <span className="db-src-name">{src.name}</span>
                          <div className="db-src-bar-track">
                            <div className="db-src-bar-fill"
                              style={{
                                width: `${pct(src.count, srcTotal)}%`,
                                background: SRC_COLORS[src.name] || '#8a8a8e',
                              }}/>
                          </div>
                          <span className="db-src-count">{fmtN(src.count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Tables */}
                {topTables.length > 0 && (
                  <div className="db-card">
                    <div className="db-card-hd">
                      <div className="db-card-title"><Table2 size={14}/> Top Tables</div>
                      <div className="db-card-meta">by revenue</div>
                    </div>
                    <div className="db-card-bd">
                      {topTables.map((t, i) => (
                        <div key={i} className="db-table-row">
                          <div className={`db-table-badge ${i === 0 ? 'top' : ''}`}>
                            {i === 0 ? '★' : `T${i + 1}`}
                          </div>
                          <div className="db-table-info">
                            <div className="db-table-label">{t.table_label || '—'}</div>
                            <div className="db-table-sub">{fmtN(t.orders)} orders</div>
                          </div>
                          <div>
                            <div className="db-item-bar-track" style={{ width: 50 }}>
                              <div className="db-item-bar-fill"
                                style={{ width: `${pct(t.revenue, maxTableRev)}%` }}/>
                            </div>
                          </div>
                          <div className="db-table-rev">{fmt(t.revenue)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
